// Component: AlgoStrategyCard.jsx
// Purpose: Grid view card for Algo Trading dashboard — matches StrategyHub card design

import React, { useMemo } from 'react';
import AlgoMiniChart from './AlgoMiniChart';
import LivePriceBadge from '../../../components/trading/LivePriceBadge';
import { computeAnalytics } from '../../../utils/tradingAnalytics';
//* E5b — the ONLY place this card may derive "something's wrong"; never re-derive inline.
import { deriveLivenessNote, livenessToneColor } from '../utils/liveness';

// Component: Deployment lifecycle reason copy
// Keep machine codes and instrument identifiers off the user-facing surface.
const stopReasonCopy = {
  instrument_expired: 'Stopped — instrument expired',
  instrument_inactive: 'Stopped — instrument no longer tradable',
};

const OPEN_POSITION_COPY = 'Instrument is no longer tradable — close the open position; the deployment will stop automatically';

/*>>> STATUS CONFIGURATION — mirrors StrategyHub <<<*/
const statusStyles = {
  running: {
    bg: 'bg-profit dark:bg-profit/10',
    text: 'text-profit dark:text-profit',
    border: 'border-profit dark:border-profit/20',
    dot: 'bg-profit',
    label: 'Running',
    pulse: true,
  },
  stopped: {
    bg: 'bg-loss dark:bg-loss/10',
    text: 'text-loss dark:text-loss',
    border: 'border-loss dark:border-loss/20',
    dot: 'bg-loss',
    label: 'Stopped',
    pulse: false,
  },
};

const modeStyles = {
  live: {
    bg: 'bg-profit dark:bg-profit/10',
    text: 'text-profit dark:text-profit',
    border: 'border-profit dark:border-profit/20',
    label: 'Live',
  },
  paper: {
    bg: 'bg-primary dark:bg-primary/10',
    text: 'text-primary dark:text-primary',
    border: 'border-primary dark:border-primary/20',
    label: 'Paper',
  },
};

/*>>> RELATIVE TIME HELPER <<<*/
function timeAgo(dateStr) {
  if (!dateStr) return '';
  const seconds = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function AlgoStrategyCard({ strategy, onClick, onStop }) {
  const {
    id,
    name,
    version,
    mode = 'paper',
    status = 'stopped',
    timeframe,
    symbol,
    instrumentType,
    isContinuous,
    rolloverDays,
    startTime,
    stoppedAt,
    pnl = 0,
    winRate = 0,
    totalTrades = 0,
    openOrdersCount = 0,
    position,
    strategyOrders = [],
  } = strategy || {};

  //--> Compute per-trade P&L series for the equity curve sparkline
  const tradePnls = useMemo(() => {
    if (!strategyOrders || strategyOrders.length === 0) return null;
    const analytics = computeAnalytics(strategyOrders);
    return analytics.tradePnls.length >= 2 ? analytics.tradePnls : null;
  }, [strategyOrders]);

  const sCfg = statusStyles[status] || statusStyles.stopped;
  const mCfg = modeStyles[mode] || modeStyles.paper;
  const isStopped = status === 'stopped';
  const pnlPositive = pnl >= 0;
  const stopReason = strategy?.stopReason ?? strategy?.stop_reason;
  const deadInstrument = strategy?.deadInstrument ?? strategy?.dead_instrument;
  const stoppedLabel = isStopped ? stopReasonCopy[stopReason] : null;
  const showOpenPositionReason = (
    status === 'running'
    && deadInstrument?.reason_code === 'dead_instrument_position_open'
  );

  //--> The ONE liveness label (utils/liveness.js) — null only for non-running cards; a healthy
  //*   running card renders `Live` as an ADDITIONAL line below the timestamp.
  const livenessNote = deriveLivenessNote(strategy);

  //! F4 (144-1): a NULL net P&L (charges unresolved) renders as 'Unresolved', never Rs 0.
  const pnlUnresolved = strategy?.pnlUnresolved === true;
  const pnlClass = pnlUnresolved ? 'text-fg-3' : (pnlPositive ? 'text-profit' : 'text-loss');
  const formattedPnl = pnlUnresolved ? 'Unresolved' : `${pnlPositive ? '+' : ''}${pnl.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  })}`;

  return (
    <div
      onClick={() => onClick?.(id)}
      className={`
        group bg-bg-2 rounded-xl p-4 flex flex-col gap-3
        shadow-sm hover:shadow-lg transition-all duration-300
        cursor-pointer relative overflow-hidden
        ${isStopped ? 'opacity-80 hover:opacity-100' : ''}
      `}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(id);
        }
      }}
    >
      {/* --> Header: name + status dot (mirrors StrategyHub) */}
      <div className="flex justify-between items-start z-10">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="ds-h3 text-fg-1">{name}</h3>
            <span className={`w-2 h-2 rounded-full ${sCfg.dot} ${sCfg.pulse ? 'animate-pulse' : ''}`} />
          </div>
          {/* --> Meta line: badges + symbol + timeframe + version */}
          <div className="flex items-center gap-2 text-xs font-medium flex-wrap">
            <span className={`ds-badge xs ${mode === 'live' ? 'profit' : 'info'}`}>
              {mCfg.label}
            </span>
            {symbol && (
              <span className="text-fg-2 font-mono">{symbol}</span>
            )}
            {instrumentType && instrumentType !== 'UNKNOWN' && (
              <span className="text-[10px] bg-bg-3 px-1 rounded text-fg-2">{instrumentType}</span>
            )}
            {isContinuous && (
              <span className="text-[10px] bg-bg-3 px-1 rounded text-fg-2" title={`Continuous contract — rolls ${rolloverDays} days before expiry`}>
                CONT ({rolloverDays}d)
              </span>
            )}
            {timeframe && (
              <>
                <span className="text-fg-2">&middot;</span>
                <span className="text-fg-2">{timeframe}</span>
              </>
            )}
            {version && (
              <>
                <span className="text-fg-2">&middot;</span>
                <span className="text-fg-2">{version}</span>
              </>
            )}
          </div>
        </div>

        {/* --> Right column: always-visible live price badge + hover-only action buttons */}
        <div className="flex flex-col items-end gap-1">
          <LivePriceBadge deploymentId={id} compact showSymbol={false} />
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {status === 'running' ? (
            <button
              onClick={(e) => { e.stopPropagation(); onStop?.(strategy); }}
              className="p-1.5 rounded-lg text-fg-3 hover:text-loss hover:bg-loss/10 transition-colors"
              title="Stop strategy"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
              </svg>
            </button>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); onClick?.(id); }}
              className="p-1.5 rounded-lg text-fg-3 hover:text-profit hover:bg-profit/10 transition-colors"
              title="Manage — redeploy from the strategy"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          )}
          </div>
        </div>
      </div>

      {/* --> Chart area with P&L overlay */}
      <div className="relative">
        <AlgoMiniChart pnl={pnl} heightClass="h-20" strategyId={id} tradePnls={tradePnls} />
        {pnl !== 0 && !pnlUnresolved && (
          <div className={`absolute bottom-0 right-0 text-xs font-bold ${pnlPositive ? 'text-profit' : 'text-loss'} bg-bg-3/80 px-1.5 rounded backdrop-blur-sm`}>
            {formattedPnl}
          </div>
        )}
      </div>

      {/* --> Metrics: single compact row */}
      <div className="flex items-stretch gap-3 py-2 border-t border-dashed border-[var(--divider)]">
        <div className="flex-1 min-w-0">
          <p className="ds-eyebrow text-fg-3">P&L</p>
          <p className={`text-sm font-mono font-bold truncate ${pnlClass}`} data-testid="strategy-pnl">
            {formattedPnl}
          </p>
        </div>
        <div className="flex-1 min-w-0">
          <p className="ds-eyebrow text-fg-3">Win</p>
          <p className={`text-sm font-mono font-bold ${
            winRate >= 0.6 ? 'text-profit' : winRate >= 0.4 ? 'text-warning' : totalTrades > 0 ? 'text-loss' : 'text-fg-3'
          }`}>
            {totalTrades > 0 ? `${(winRate * 100).toFixed(0)}%` : '--'}
          </p>
        </div>
        <div className="flex-1 min-w-0">
          <p className="ds-eyebrow text-fg-3">Trades</p>
          <p className={`text-sm font-mono font-bold ${totalTrades > 0 ? 'text-fg-1' : 'text-fg-3'}`}>
            {totalTrades > 0 ? totalTrades : '--'}
          </p>
        </div>
        <div className="flex-1 min-w-0">
          <p className="ds-eyebrow text-fg-3">Open</p>
          <p className={`text-sm font-mono font-bold ${openOrdersCount > 0 ? 'text-fg-1' : 'text-fg-3'}`}>
            {openOrdersCount > 0 ? openOrdersCount : '--'}
          </p>
        </div>
      </div>

      {/* --> Inline position (conditional) */}
      {position && (
        <div className="py-2 border-t border-dashed border-[var(--divider)]">
          <p className="text-xs text-fg-3">
            <span className={`font-semibold ${position.side === 'LONG' ? 'text-profit' : 'text-loss'}`}>
              {position.side}
            </span>
            {' '}{position.qty} @ {'\u20B9'}{position.avg_entry_price?.toLocaleString('en-IN')}
            {' '}&middot;{' '}
            <span className={position.unrealized_pnl >= 0 ? 'text-profit' : 'text-loss'}>
              {position.unrealized_pnl >= 0 ? '+' : ''}
              {'\u20B9'}{position.unrealized_pnl?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </span>
          </p>
        </div>
      )}

      {/* Component: LIVE dead-instrument action-required reason */}
      {showOpenPositionReason && (
        <p className="text-xs font-medium text-warning" role="status">
          {OPEN_POSITION_COPY}
        </p>
      )}

      {/* --> Footer: timestamp (+ liveness note when unhealthy) + details link */}
      <div className="flex items-center justify-between pt-1">
        {/*! The liveness label is an ADDITIONAL sibling line, never a mutation of the
            timestamp line; non-running cards render exactly the single <p>. */}
        {livenessNote ? (
          <div className="flex flex-col gap-0.5 min-w-0">
            <p className="text-[10px] text-fg-3 italic">
              {stoppedLabel || (isStopped ? `Stopped ${timeAgo(stoppedAt)}` : `Started ${timeAgo(startTime)}`)}
            </p>
            <p
              className="text-[10px] italic truncate"
              style={{ color: livenessToneColor(livenessNote.tone) }}
            >
              {livenessNote.text}
            </p>
          </div>
        ) : (
          <p className="text-[10px] text-fg-3 italic">
            {stoppedLabel || (isStopped ? `Stopped ${timeAgo(stoppedAt)}` : `Started ${timeAgo(startTime)}`)}
          </p>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onClick?.(id); }}
          className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-fg-3 hover:text-fg-1 hover:bg-bg-3 transition-colors"
        >
          Details
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default AlgoStrategyCard;
